import React, { useState, useEffect, useContext } from 'react';
import Section from '@latitude/section-rebrand';
import FeaturesSlider from '@/components/FeaturesSlider/FeaturesSlideV2';
import { PageContext } from '@/context/PageContext';
import { ImportantInformation } from '@latitude/important-information-rebrand';
import Layout from '@/components/layout';
import MobileAppInstallPrompts from '@latitude/mobile-app-install-prompt/MobileAppInstallPrompt';
import PageData from '@/data/pages/calculators-and-tools/personal-loan-repayment-calculator.json';
import StickyNavigation from '@/components/StickyNavigation/StickyNavigationV2';
import PersonalLoanCalculatorV2 from '@/components/PersonalLoanCalculatorRebrandV2/PersonalLoanCalculator';
import {
  PL_APPLY_CTA_TRACK_ID,
  PL_SYMPLE_MINIMUM_AMOUNT,
  PL_SYMPLE_DEFAULT_PERIOD
} from '@/utils/constants';
import {
  redirectUrl,
  softQuoteHrefFunc
} from '@/components/EstimateRateWidget/utils';
import PromoBanner from '@/components/lab-components/PromoBanner/PromoBannerV2';
import { AnalyticsContext } from 'latitude-analytics';
import CardCollection from '@/components/lab-components/CardCollection/CardCollectionV2';
import Typography from '@latitude/typography';
import { REBRAND_COLOR, PADDING } from '@latitude/core/utils/constants';
import HeroV2 from '@/components/Hero/HeroV2/HeroV2';

const CTA_LABEL = 'Get your personalised rate';

const featureSliderJson = [
  {
    heading: 'Why choose a Latitude loan?',
    featureCards: PageData.content.whyChooseLatitude
  }
];

// eslint-disable-next-line arrow-body-style
const PLCalculatorPage = ({ location }) => {
  // soft quote
  const [softQuoteHref, setSoftQuoteHref] = useState();
  useEffect(() => {
    sessionStorage.setItem('purpose', 'purpose'); // reset session val used in `EstimateRateWidgetSection`
    sessionStorage.setItem('loanAmount', PL_SYMPLE_MINIMUM_AMOUNT.toString());
    sessionStorage.setItem('repaymentPeriod', PL_SYMPLE_DEFAULT_PERIOD);
    setSoftQuoteHref(redirectUrl('au', true));
  }, []);

  /** Contentful Data */
  const contentfulPageData = useContext(PageContext);
  const state = contentfulPageData?.updatedBody || {};
  /** - END - */

  const [analytics] = useContext(AnalyticsContext);
  useEffect(() => {
    const sourceCode = localStorage.getItem('source_code') || 'IDAAA';
    // adding pageData to analytics
    analytics.pageData = {
      page_pageTitle: 'personal-loan-repayment-calculator',
      page_pageType: 'calculator-page',
      page_siteSection: 'loans',
      page_siteSubsection: 'personal-loan-repayment-calculator',
      product_productid: ['PLAULF-WEB'],
      application_sourcecode: sourceCode || 'IDAAA'
    };
  }, []);

  return (
    <Layout
      location={location}
      title={PageData.title}
      metaDesc={PageData.metaDesc}
      canonical="/personal-loan-repayment-calculator/"
    >
      <main
        className="navigation-spacer"
        css={`
          overflow-x: hidden;
        `}
      >
        <MobileAppInstallPrompts />
        <HeroV2
          {...(state?.heroBannerData?.[0] ||
            PageData?.contentful?.heroBannerData?.[0])}
        />
        <StickyNavigation
          items={PageData?.contentful?.inPageNavData.items}
          enableAnchorUpdate={true}
          phoneNumber={PageData.content.phoneNumber}
          ctaHref={softQuoteHrefFunc(softQuoteHref)}
          ctaText={CTA_LABEL}
          offsetElem="[data-sticky-navigation-offset]"
          target="_self"
          trackEventData={{
            category: 'cta',
            action: 'quote-link',
            location: 'sticky'
          }}
          trackId="sticky-get-started"
          {...state?.inPageNavData?.[0]}
        />
        <section
          id="calculator"
          css={`
            padding: 0px !important;
          `}
        >
          <div
            id="calculator"
            css={`
              background-color: ${REBRAND_COLOR.WHITE};
              padding-top: 32px;
              padding-bottom: 32px;

              && .typography-base__sh1 {
                margin-bottom: 30px !important;
              }
            `}
          >
            <Section>
              <Typography.SH1 color={REBRAND_COLOR.BLACK} semibold>
                Get an estimate of your repayments
              </Typography.SH1>

              <PersonalLoanCalculatorV2
                loans1
                ctaButtonLabel="Get your personalised rate"
                applyCTATrackId={PL_APPLY_CTA_TRACK_ID}
              />
            </Section>
          </div>
        </section>
        <FeaturesSlider
          id="why-latitude"
          backgroundColor={
            state?.featureSliderData?.[0]?.backgroundColor?.value ||
            REBRAND_COLOR.WHITE
          }
          key={
            (
              state?.featureSliderData?.[0] ||
              PageData?.contentful?.featureSliderData?.[0]
            ).heading
          }
          heading={
            (
              state?.featureSliderData?.[0] ||
              PageData?.contentful?.featureSliderData?.[0]
            ).heading
          }
          className="why-choose bg-f8f8f8"
          subheading={
            (
              state?.featureSliderData?.[0] ||
              PageData?.contentful?.featureSliderData?.[0]
            ).description
          }
          data={
            (
              state?.featureSliderData?.[0] ||
              PageData?.contentful?.featureSliderData?.[0]
            ).featureCards
          }
        />
        <PromoBanner
          {...(state?.promoBannerData?.[0] ||
            PageData?.contentful?.promoBannerData?.[0])}
        />

        <CardCollection
          id="applying-is-simple"
          data={
            state?.cardCollectionData?.[0] ||
            PageData?.contentful?.cardCollectionData?.[0]
          }
        />

        <PromoBanner
          id="eligibility-criteria"
          {...(state?.promoBannerData?.[1] ||
            PageData?.contentful?.promoBannerData?.[1])}
        />

        <CardCollection
          id="explore-loans"
          data={
            state?.cardCollectionData?.[1] ||
            PageData?.contentful?.cardCollectionData?.[1]
          }
        />

        <ImportantInformation
          data={require('@/data/json/disclaimer/personal-loan1.json')}
          css={`
            overflow-x: hidden;
            background-color: ${REBRAND_COLOR.STONE};
            .row {
              @media (min-width: 1600px) {
                padding-left: ${PADDING.P32};
                padding-right: ${PADDING.P32};
              }
            }
          `}
          {...{
            ...state?.importantInfoData?.[0],
            sectionOneColummClasses: 'col-12 col-lg-6',
            sectionTwoColummClasses: 'col-12 col-lg-6'
          }}
        />
      </main>
    </Layout>
  );
};

export default PLCalculatorPage;
